import { Button } from "@package/subframe/components/Button";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import { TextArea } from "@package/subframe/components/TextArea";
import { FeatherCheckCircle } from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useOfflineIncidentSignoff } from "@/hooks/use-offline-incident-signoff";
import { invalidateMonitoringQueries } from "@/lib/monitoring/invalidate-monitoring-queries";

interface SignOffIncidentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  incidentId: string;
  coldStorageName: string;
  /**
   * Called once the sign-off has landed and every affected surface has been
   * refreshed. The incident is resolved by then, so the caller decides where to
   * send the manager instead of leaving them on the record they just closed.
   */
  onSignedOff: () => void;
}

export function SignOffIncidentDialog({
  open,
  onOpenChange,
  incidentId,
  coldStorageName,
  onSignedOff,
}: SignOffIncidentDialogProps) {
  const queryClient = useQueryClient();
  const { signOff } = useOfflineIncidentSignoff(incidentId);
  const [notes, setNotes] = useState("");
  // Not the hook's own `isPending`: that clears as soon as the write is queued,
  // which is before the awaited refresh below settles — it would re-enable
  // "Sign off" mid-flight and let a second submission through.
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    // Snapshot the connection state BEFORE awaiting the hook. Reading after
    // would let the queued sync flip us back to online mid-await and lie to
    // the manager about whether their submission is fully through.
    const wasOnline =
      typeof navigator !== "undefined" ? navigator.onLine : true;

    setSubmitting(true);
    try {
      // Persist + optimistically drop from the open-incidents cache + enqueue.
      // The hook triggers an immediate sync when online; when offline the
      // queue worker picks it up on reconnect.
      await signOff(notes);
    } catch {
      toast.error("Failed to sign off incident");
      setSubmitting(false);
      return;
    }

    if (wasOnline) {
      toast.success(`${coldStorageName} incident signed off`);
    } else {
      toast.success(
        `${coldStorageName} sign-off saved — will sync when you reconnect`,
      );
    }

    // Awaited before leaving so the page we return to renders the resolution
    // rather than flashing the open state it still has cached.
    await invalidateMonitoringQueries(queryClient);

    setNotes("");
    setSubmitting(false);
    // Close before handing over: unmounting a still-open Radix dialog by route
    // change leaves `pointer-events: none` stuck on the body.
    onOpenChange(false);
    onSignedOff();
  };

  const handleCancel = () => {
    setNotes("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} className="max-w-[480px] p-0">
        <DialogTitle className="sr-only">Sign off incident</DialogTitle>
        <div className="flex h-full w-full flex-col items-center gap-4 bg-white">
          <div className="flex w-full max-w-[448px] flex-col items-center gap-6 p-4">
            <IconWithLogoBackground
              variant="success"
              size="x-large"
              icon={<FeatherCheckCircle />}
            />
            <div className="flex w-full flex-col items-center gap-2">
              <span className="text-center text-heading-2 font-heading-2 text-default-font">
                Sign off incident?
              </span>
              <span className="text-center text-body font-body text-subtext-color">
                Confirm that <strong>{coldStorageName}</strong> is back within
                its target range and any remedial work has been completed.
              </span>
            </div>
            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-body-bold font-body-bold text-neutral-900">
                Remedial-action notes
                <span className="text-caption font-caption text-subtext-color ml-1">
                  (optional)
                </span>
              </span>
              <TextArea className="h-auto w-full flex-none" variant="outline">
                <TextArea.Input
                  placeholder="e.g. Replaced door seal; engineer attended at 14:30; temp stable at 4°C since."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="min-h-[100px]"
                  maxLength={4000}
                />
              </TextArea>
            </div>
          </div>
          <div className="flex w-full flex-col items-center gap-3 bg-white px-4 py-4">
            <Button
              className="h-14 w-80 flex-none"
              variant="neutral-secondary"
              size="extra-large"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              className="h-14 w-80 flex-none"
              size="extra-large"
              onClick={handleConfirm}
              disabled={submitting}
            >
              {submitting ? "Signing off..." : "Sign off"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
